import { cacheOperatorRows, readOperatorRows } from "@/lib/operator-drafts";
import { useOperatorDraft } from "@/hooks/useOperatorDraft";
import { queueScope } from "@/lib/offline-queue";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOffline } from "@/contexts/OfflineContext";
import { apiFetch } from "@/lib/api-fetch";
import { hapticSuccess } from "@/lib/native-init";
import {
  Thermometer,
  Check,
  CheckCircle2,
  ArrowLeft,
  Flame,
  Target,
  Search,
  Filter,
  ArrowUpDown,
  FolderTree,
  Plus,
  Camera,
  RotateCcw,
  X,
  Sparkles,
  ChevronDown,
  ExternalLink,
  AlertTriangle,
  FileImage,
  UploadCloud,
} from "lucide-react";
import PhotoCapture from "@/components/PhotoCapture";
import { compressPhoto } from "@/lib/compress-photo";
import { cn } from "@/lib/utils";

type WindrowOption = {
  id: number;
  row_id: string;
  name: string;
  status: string;
};

type TemperatureRecord = {
  id: number;
  windrow_id: number;
  windrow_name: string;
  windrow_row_id: string;
  temperature: number;
  probe_location: string | null;
  photo_url: string | null;
  notes: string | null;
  recorded_by: string | null;
  recorded_at: string;
};

type TempSortBy = "recorded_at" | "temperature" | "windrow_name" | "recorded_by";
type TempGroupBy = "none" | "windrow" | "day" | "operator";

const PROBE_LOCATIONS = ["center", "north", "south", "east", "west"];
const COMPLIANCE_MIN_F = 131;

function compareValues(a: string | number, b: string | number) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

function toDayKey(dateString: string) {
  return dateString.slice(0, 10);
}

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function LogTemperaturePage() {
  const location = useLocation();
  const isOperatorView = location.pathname.startsWith("/app/operator");

  if (isOperatorView) {
    return <OperatorTemperatureLogger />;
  }

  return <ManagerTemperatureWorkspace />;
}

function ManagerTemperatureWorkspace() {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectedWindrow = searchParams.get("windrow");
  const recordFocusParam = searchParams.get("record");

  const [records, setRecords] = useState<TemperatureRecord[]>([]);
  const [windrows, setWindrows] = useState<WindrowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [windrowId, setWindrowId] = useState(preselectedWindrow || "");
  const [temperature, setTemperature] = useState("");
  const [probeLocation, setProbeLocation] = useState("center");
  const [recordedBy, setRecordedBy] = useState(user?.name || user?.email || "Manager");
  const [notes, setNotes] = useState("");

  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  /** Table filter only — `windrow` query param is reserved for the add form pre-select */
  const [windrowFilter, setWindrowFilter] = useState(() => searchParams.get("pile") || "all");
  const [complianceFilter, setComplianceFilter] = useState<"all" | "below" | "compliant">(
    () => (searchParams.get("compliance") as "all" | "below" | "compliant") || "all"
  );
  const [probeFilter, setProbeFilter] = useState<string>(() => searchParams.get("probe") || "all");
  const [sortBy, setSortBy] = useState<TempSortBy>("recorded_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [groupBy, setGroupBy] = useState<TempGroupBy>("none");
  const [addFormOpen, setAddFormOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

  const updateQuery = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v == null || v === "") next.delete(k);
        else next.set(k, v);
      }
      return next;
    });
  }, [setSearchParams]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [temperaturesRes, windrowsRes] = await Promise.all([
        apiFetch("/api/temperatures", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/windrows/active", { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (!temperaturesRes.ok) {
        throw new Error(`Failed to load temperatures (${temperaturesRes.status})`);
      }
      if (!windrowsRes.ok) {
        throw new Error(`Failed to load windrows (${windrowsRes.status})`);
      }

      const temperaturesData = await temperaturesRes.json();
      const windrowsData = await windrowsRes.json();

      const nextWindrows = windrowsData.windrows || [];
      setRecords(temperaturesData.temperatures || []);
      setWindrows(nextWindrows);

      if (!windrowId && !preselectedWindrow && nextWindrows.length > 0) {
        setWindrowId(String(nextWindrows[0].id));
      }
    } catch (err: any) {
      const msg = err?.message || "Failed to load data";
      const hint = /404|502|503|Failed to fetch/.test(msg)
        ? " — Is the API server running? From project root run: npm run dev"
        : "";
      setError(msg + hint);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token]);

  useEffect(() => {
    if (!recordFocusParam || loading) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(`temp-record-${recordFocusParam}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => window.clearTimeout(t);
  }, [recordFocusParam, loading, records.length]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      updateQuery({ q: search.trim() || null });
    }, 350);
    return () => window.clearTimeout(id);
  }, [search, updateQuery]);

  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupBy]);

  const stats = useMemo(() => {
    const total = records.length;
    const below = records.filter((r) => r.temperature < COMPLIANCE_MIN_F).length;
    const avg = total > 0 ? Math.round(records.reduce((sum, r) => sum + r.temperature, 0) / total) : 0;
    const uniqueWindrows = new Set(records.map((r) => r.windrow_id)).size;
    return { total, below, avg, uniqueWindrows };
  }, [records]);

  const sortedRows = useMemo(() => {
    const searchLower = search.trim().toLowerCase();

    const filtered = records.filter((record) => {
      if (windrowFilter !== "all" && String(record.windrow_id) !== windrowFilter) {
        return false;
      }
      if (complianceFilter === "below" && record.temperature >= COMPLIANCE_MIN_F) {
        return false;
      }
      if (complianceFilter === "compliant" && record.temperature < COMPLIANCE_MIN_F) {
        return false;
      }
      if (probeFilter !== "all" && (record.probe_location || "").toLowerCase() !== probeFilter.toLowerCase()) {
        return false;
      }
      if (!searchLower) {
        return true;
      }

      const haystack = [
        record.windrow_name,
        record.windrow_row_id,
        record.probe_location || "",
        record.recorded_by || "",
        record.notes || "",
        String(record.temperature),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(searchLower);
    });

    return [...filtered].sort((a, b) => {
      let left: string | number;
      let right: string | number;

      if (sortBy === "recorded_at") {
        left = new Date(a.recorded_at).getTime();
        right = new Date(b.recorded_at).getTime();
      } else if (sortBy === "temperature") {
        left = a.temperature;
        right = b.temperature;
      } else if (sortBy === "windrow_name") {
        left = `${a.windrow_name} ${a.windrow_row_id}`;
        right = `${b.windrow_name} ${b.windrow_row_id}`;
      } else {
        left = a.recorded_by || "";
        right = b.recorded_by || "";
      }

      const value = compareValues(left, right);
      return sortDirection === "asc" ? value : -value;
    });
  }, [records, search, windrowFilter, complianceFilter, probeFilter, sortBy, sortDirection]);

  const groupedRows = useMemo(() => {
    if (groupBy === "none") {
      return [{ key: "all", label: `All Records (${sortedRows.length})`, rows: sortedRows }];
    }

    const map = new Map<string, TemperatureRecord[]>();

    for (const row of sortedRows) {
      let key = "";
      if (groupBy === "windrow") {
        key = `${row.windrow_name} (${row.windrow_row_id})`;
      } else if (groupBy === "day") {
        key = toDayKey(row.recorded_at);
      } else {
        key = row.recorded_by || "Unassigned";
      }

      const group = map.get(key);
      if (group) {
        group.push(row);
      } else {
        map.set(key, [row]);
      }
    }

    return Array.from(map.entries()).map(([key, rows]) => ({
      key,
      label: groupBy === "day" ? `${new Date(`${key}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${rows.length})` : `${key} (${rows.length})`,
      rows,
    }));
  }, [sortedRows, groupBy]);

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!windrowId || !temperature) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFetch(`/api/windrows/${windrowId}/temperatures`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          temperature: Number(temperature),
          probeLocation,
          notes: notes || null,
          recordedBy: recordedBy || null,
          photoUrl: null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to log temperature (${res.status})`);
      }

      setTemperature("");
      setNotes("");
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to log temperature");
    } finally {
      setSubmitting(false);
    }
  };

  const clearTableFilters = () => {
    setSearch("");
    setWindrowFilter("all");
    setComplianceFilter("all");
    setProbeFilter("all");
    updateQuery({ q: null, pile: null, compliance: null, probe: null, record: null });
  };

  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="heading-display text-3xl text-foreground">Temperature Records</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Table-based manager logging with fast filters, sorting, and grouping. Use stat cards as shortcuts, collapse panels to focus on the table, and open a windrow for full history.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          type="button"
          onClick={clearTableFilters}
          className="bg-card border border-border rounded-xl p-4 text-left hover:border-forest-300 dark:hover:border-forest-800 transition-colors"
          title="Clear search and table filters"
        >
          <p className="label-caps text-muted-foreground">Total Records</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.total}</p>
          <p className="text-[10px] text-muted-foreground mt-2">Click to show all</p>
        </button>
        <button
          type="button"
          onClick={() => {
            setComplianceFilter("below");
            updateQuery({ compliance: "below" });
          }}
          className="bg-card border border-border rounded-xl p-4 text-left hover:border-red-300 dark:hover:border-red-800 transition-colors"
          title="Filter table to readings below 131°F"
        >
          <p className="label-caps text-muted-foreground">Below 131°F</p>
          <p className="stat-value text-3xl text-red-600 dark:text-red-400 mt-2">{stats.below}</p>
          <p className="text-[10px] text-muted-foreground mt-2">Click to filter</p>
        </button>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Average Temp</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.total > 0 ? `${stats.avg}°F` : "-"}</p>
        </div>
        <Link
          to="/app/windrows"
          className="bg-card border border-border rounded-xl p-4 block hover:border-forest-300 dark:hover:border-forest-800 transition-colors"
          title="Manage windrows"
        >
          <p className="label-caps text-muted-foreground">Active Windrows</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.uniqueWindrows}</p>
          <p className="text-[10px] text-forest-600 dark:text-forest-400 mt-2 inline-flex items-center gap-1">
            Open list <ExternalLink size={10} />
          </p>
        </Link>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setAddFormOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-muted/40 transition-colors"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Plus size={16} className="text-forest-600 shrink-0" />
            Add Temperature Record
          </div>
          <ChevronDown
            size={18}
            className={cn("text-muted-foreground shrink-0 transition-transform", addFormOpen && "rotate-180")}
            aria-hidden
          />
        </button>
        {addFormOpen && (
      <form onSubmit={handleAddRecord} className="px-4 sm:px-5 pb-5 pt-0 space-y-4 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <select
            value={windrowId}
            onChange={(e) => setWindrowId(e.target.value)}
            className="lg:col-span-2 px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            required
          >
            <option value="">Select windrow</option>
            {windrows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.row_id})
              </option>
            ))}
          </select>

          <input
            type="number"
            min="0"
            max="250"
            step="0.1"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            placeholder="Temp °F"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            required
          />

          <select
            value={probeLocation}
            onChange={(e) => setProbeLocation(e.target.value)}
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
          >
            {PROBE_LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={recordedBy}
            onChange={(e) => setRecordedBy(e.target.value)}
            placeholder="Recorded by"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
          />

          <button
            type="submit"
            disabled={submitting || !windrowId || !temperature}
            className="px-4 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>

        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
        />
      </form>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setFiltersOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-muted/40 transition-colors"
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <Filter size={16} className="text-muted-foreground shrink-0" />
            <span className="text-sm font-semibold text-foreground">Filters &amp; display</span>
            <span className="text-xs text-muted-foreground">
              {sortedRows.length} matching — filters sync to the address bar for sharing
            </span>
          </div>
          <ChevronDown
            size={18}
            className={cn("text-muted-foreground shrink-0 transition-transform", filtersOpen && "rotate-180")}
            aria-hidden
          />
        </button>
        {filtersOpen && (
        <div className="px-4 sm:px-5 pb-5 pt-0 space-y-4 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <label className="lg:col-span-2 flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search windrow, operator, notes..."
              className="w-full py-2 text-sm bg-transparent outline-none"
            />
          </label>

          <select
            value={windrowFilter}
            onChange={(e) => {
              const v = e.target.value;
              setWindrowFilter(v);
              updateQuery({ pile: v === "all" ? null : v });
            }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="all">All windrows</option>
            {windrows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.row_id})
              </option>
            ))}
          </select>

          <select
            value={complianceFilter}
            onChange={(e) => {
              const v = e.target.value as "all" | "below" | "compliant";
              setComplianceFilter(v);
              updateQuery({ compliance: v === "all" ? null : v });
            }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="all">All temperatures</option>
            <option value="below">Below 131°F</option>
            <option value="compliant">131°F and above</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as TempSortBy)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="recorded_at">Sort by date</option>
            <option value="temperature">Sort by temperature</option>
            <option value="windrow_name">Sort by windrow</option>
            <option value="recorded_by">Sort by operator</option>
          </select>

          <button
            type="button"
            onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm inline-flex items-center justify-center gap-1"
          >
            <ArrowUpDown size={14} />
            {sortDirection === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <span className="whitespace-nowrap font-medium text-foreground">Probe</span>
            <select
              value={probeFilter}
              onChange={(e) => {
                const v = e.target.value;
                setProbeFilter(v);
                updateQuery({ probe: v === "all" ? null : v });
              }}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
            >
              <option value="all">All positions</option>
              {PROBE_LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={clearTableFilters}
            className="text-xs font-medium text-forest-600 dark:text-forest-400 hover:underline"
          >
            Reset filters
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FolderTree size={14} className="text-muted-foreground" />
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as TempGroupBy)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="none">No grouping</option>
            <option value="windrow">Group by windrow</option>
            <option value="day">Group by day</option>
            <option value="operator">Group by operator</option>
          </select>
        </div>
        </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading records...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : sortedRows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No temperature records found for current filters.</div>
        ) : (
          <div className="divide-y divide-border">
            {groupedRows.map((group) => {
              const groupCollapsed = groupBy !== "none" && collapsedGroups.has(group.key);
              return (
              <section key={group.key} className="p-4">
                {groupBy !== "none" && (
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapsed(group.key)}
                    className="flex items-center gap-2 w-full text-left mb-2 rounded-lg py-1 -mx-1 px-1 hover:bg-muted/50 transition-colors"
                  >
                    <ChevronDown
                      size={16}
                      className={cn("text-muted-foreground shrink-0 transition-transform", groupCollapsed && "-rotate-90")}
                      aria-hidden
                    />
                    <h3 className="text-sm font-semibold text-foreground">{group.label}</h3>
                  </button>
                )}
                {!groupCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2.5">Recorded At</th>
                        <th className="px-3 py-2.5">Windrow</th>
                        <th className="px-3 py-2.5">Temp</th>
                        <th className="px-3 py-2.5">Probe</th>
                        <th className="px-3 py-2.5">Recorded By</th>
                        <th className="px-3 py-2.5">Notes</th>
                        <th className="px-3 py-2.5">Photo</th>
                        <th className="px-3 py-2.5 w-[1%] whitespace-nowrap text-right">Open</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((record) => {
                        const below = record.temperature < COMPLIANCE_MIN_F;
                        const focused = recordFocusParam === String(record.id);
                        return (
                          <tr
                            key={record.id}
                            id={`temp-record-${record.id}`}
                            className={cn(
                              "border-b border-border/60 last:border-0 transition-shadow",
                              below ? "bg-red-50/60 dark:bg-red-950/20" : "",
                              focused && "ring-2 ring-inset ring-forest-500/70 dark:ring-forest-400/50"
                            )}
                          >
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(record.recorded_at)}</td>
                            <td className="px-3 py-2.5">
                              <Link
                                to={`/app/windrows/${record.windrow_id}`}
                                className="font-medium text-foreground hover:text-forest-600 dark:hover:text-forest-400 hover:underline"
                              >
                                {record.windrow_name}
                              </Link>
                              <p className="text-xs text-muted-foreground font-mono">{record.windrow_row_id}</p>
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <span className={`font-semibold ${below ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>
                                {record.temperature}°F
                              </span>
                            </td>
                            <td className="px-3 py-2.5 capitalize text-muted-foreground">{record.probe_location || "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{record.recorded_by || "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{record.notes || "-"}</td>
                            <td className="px-3 py-2.5">
                              {record.photo_url ? (
                                <a
                                  href={record.photo_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-forest-600 dark:text-forest-400 hover:underline"
                                >
                                  <Camera size={12} /> View
                                </a>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
                                  title="Scroll to this row and highlight it"
                                  onClick={() => {
                                    updateQuery({ record: String(record.id) });
                                    window.requestAnimationFrame(() => {
                                      document.getElementById(`temp-record-${record.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    });
                                  }}
                                >
                                  Focus
                                </button>
                                <Link
                                  to={`/app/windrows/${record.windrow_id}`}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-forest-600 dark:text-forest-400 hover:underline"
                                  title="Windrow detail and full history"
                                >
                                  <ExternalLink size={12} />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
              </section>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function OperatorTemperatureLogger() {
  const { token, user } = useAuth();
  const { isOnline, queueAction, refreshCount } = useOffline();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedWindrow = searchParams.get("windrow");

  const [windrows, setWindrows] = useState<WindrowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<{ queued: boolean; photoSaved: boolean } | null>(null);

  const [windrowId, setWindrowId] = useState(preselectedWindrow || "");
  const draft = useOperatorDraft(queueScope(user), "temperature", windrowId, {"temperature": "", "probeLocation": "center", "notes": ""});
  const temperature = draft.fields.temperature;
  const setTemperature = (value: string) => draft.setField("temperature", value);
  const probeLocation = draft.fields.probeLocation;
  const setProbeLocation = (value: string) => draft.setField("probeLocation", value);
  const notes = draft.fields.notes;
  const setNotes = (value: string) => draft.setField("notes", value);
  const photoFile = draft.photo;
  const setPhotoFile = draft.setPhoto;

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [aiTemperature, setAiTemperature] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzingTemperature, setAnalyzingTemperature] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  useEffect(() => {
    if (!photoFile) { setPhotoPreview(null); return; }
    const url = URL.createObjectURL(photoFile); setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  useEffect(() => {
    let cancelled = false;
    const scope = queueScope(user);
    const show = (rows: WindrowOption[]) => {
      if (cancelled) return;
      const active = rows.filter(w => w.status === 'active');
      setWindrows(active);
      if (!preselectedWindrow && active.length) setWindrowId(current => current || String(active[0].id));
    };
    void readOperatorRows(scope).then(rows => show(rows as WindrowOption[])).catch(() => {});
    apiFetch('/api/windrows', { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => { if (!response.ok) throw new Error('Unable to load rows'); return response.json(); })
      .then(data => { show(data.windrows || []); void cacheOperatorRows(scope, data.windrows || []).catch(() => {}); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, preselectedWindrow, user?.id, user?.organization_id]);

  /** Compress to web-optimized image (one file used for AI + documentation upload). */
  const handlePhotoCapture = async (file: File) => {
    setAnalyzeError(null);
    setSubmitError(null);
    setAiTemperature(null);
    setAnalyzingTemperature(true);
    try {
      const compressed = await compressPhoto(file);
      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(compressed);
      });
      const res = await apiFetch("/api/ai/read-temperature", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ image: dataUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.temperature != null) {
        setTemperature(String(data.temperature));
        setAiTemperature(Number(data.temperature));
      } else if (res.ok) {
        setAnalyzeError("Temperature could not be read from this photo. Enter it manually.");
      } else {
        const msg = data.error || (data.details ? `${data.error || "Error"}: ${data.details}` : null) || `Request failed (${res.status}). Try again or enter temperature manually.`;
        setAnalyzeError(msg);
      }
    } catch {
      setAnalyzeError("Analysis failed. Enter temperature manually.");
    } finally {
      setAnalyzingTemperature(false);
    }
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setAiTemperature(null);
    setAnalyzeError(null);
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!windrowId || !temperature || submitting || !draft.ready) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const photoBlob = photoFile ? new Blob([await photoFile.arrayBuffer()], { type: photoFile.type }) : undefined;
      await queueAction('temperature', Number(windrowId), {
        temperature: Number(temperature), probeLocation, notes: notes || null,
        recordedBy: user?.name || user?.email || null,
      }, photoBlob);
      setSuccessInfo({ queued: true, photoSaved: Boolean(photoFile) });
      await draft.clear().catch(() => {});
      setSuccess(true);
      void hapticSuccess().catch(() => {});
      setTimeout(() => navigate(`/app/operator/windrows/${windrowId}`), 1500);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to save on this device. Your form is still here.');
    } finally { setSubmitting(false); }
  };

  const tempNum = Number(temperature);
  const isAboveThreshold = temperature !== "" && tempNum >= COMPLIANCE_MIN_F;
  const isBelowThreshold = temperature !== "" && tempNum < COMPLIANCE_MIN_F;

  if (success) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <div className="w-20 h-20 bg-forest-100 dark:bg-forest-900/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Check size={36} className="text-forest-600 dark:text-forest-400" />
        </div>
        <h2 className="heading-display text-2xl text-foreground mb-2">Temperature Logged</h2>
        <p className="text-sm text-muted-foreground">
          <span className="stat-value text-lg text-foreground">{temperature}°F</span> saved on this device; upload status appears above
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 text-left">
          <div className="rounded-xl bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide font-bold text-forest-700 dark:text-forest-300">Reading</p>
            <p className="text-sm font-bold text-foreground">Saved</p>
          </div>
          <div className="rounded-xl bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide font-bold text-forest-700 dark:text-forest-300">Photo</p>
            <p className="text-sm font-bold text-foreground">
              {successInfo?.photoSaved ? (successInfo.queued ? "Queued" : "Saved") : "None"}
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Redirecting to windrow...</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <Link
        to="/app/operator"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        <ArrowLeft size={14} />
        Back to Dashboard
      </Link>

      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-border bg-gradient-to-r from-amber-50 to-amber-50/50 dark:from-amber-900/10 dark:to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <Flame size={22} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="heading-display text-xl text-foreground">Log Temperature</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Record a windrow temperature reading</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6"><fieldset disabled={!!windrowId && (!draft.ready || submitting)} className="space-y-6">
          <div className="flex items-center justify-between gap-3 text-sm"><span role="status">{draft.status}</span>{draft.status && <button type="button" className="min-h-11 underline" onClick={() => { if (window.confirm('Discard this unfinished entry and its photo?')) void draft.discard().catch(() => setSubmitError('Could not discard the draft. Try again.')); }}>Discard draft</button>}</div>
          <div>
            <label className="label-caps text-muted-foreground mb-2 block">
              Select Windrow *
            </label>
            {loading ? (
              <div className="h-11 bg-muted rounded-xl animate-pulse" />
            ) : (
              <select
                value={windrowId}
                onChange={(e) => setWindrowId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
              >
                <option value="">Choose a windrow...</option>
                {windrows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.row_id})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Step 1: Photo first (documentation + triggers AI read) */}
          <div>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <label className="label-caps text-muted-foreground block">
                  Photo Documentation
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Snap the thermometer. AI reads it, and the photo is saved with the record.
                </p>
              </div>
              {photoPreview && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800 text-[10px] font-bold text-forest-700 dark:text-forest-300 shrink-0">
                  <FileImage size={11} />
                  Ready
                </span>
              )}
            </div>
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Captured" className="w-full rounded-xl aspect-[4/3] object-cover" />
                {analyzingTemperature && (
                  <div className="absolute inset-0 bg-black/65 rounded-xl flex flex-col items-center justify-center gap-3">
                    <div className="relative w-14 h-14">
                      <div className="absolute inset-0 rounded-full border-4 border-white/20" />
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin" />
                      <Sparkles size={20} className="absolute inset-0 m-auto text-white animate-pulse" />
                    </div>
                    <span className="text-white text-sm font-bold">Reading thermometer...</span>
                    <span className="text-white/70 text-xs">AI is checking the photo</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-full text-white"
                  aria-label="Remove photo"
                >
                  <X size={16} />
                </button>
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute bottom-3 left-3 flex items-center gap-1.5 px-3 py-2 bg-black/50 backdrop-blur-sm rounded-xl text-white text-sm font-medium"
                  aria-label="Retake photo"
                >
                  <RotateCcw size={14} />
                  Retake
                </button>
              </div>
            ) : (
              <PhotoCapture
                onCapture={handlePhotoCapture}
                label="Take photo of thermometer"
              />
            )}
            {analyzeError && (
              <div className="mt-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                  {analyzeError}
                </p>
              </div>
            )}
            {!analyzingTemperature && !analyzeError && photoPreview && temperature && (
              <div className="mt-2 px-3 py-2 bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800 rounded-lg flex items-center gap-2">
                <CheckCircle2 size={15} className="text-forest-600 dark:text-forest-400" />
                <p className="text-xs text-forest-700 dark:text-forest-400 font-medium">
                  {aiTemperature != null ? `AI detected ${aiTemperature}°F` : "Photo ready"} — confirm or edit below
                </p>
              </div>
            )}
          </div>

          {/* Step 2: Temperature (auto-filled; confirm or edit) */}
          <div>
            <label className="label-caps text-muted-foreground mb-2 block">
              Temperature (°F) *
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Confirm or edit the reading, then add notes and submit.
            </p>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="250"
                placeholder={analyzingTemperature ? "..." : "145"}
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                className={`w-full px-4 py-5 stat-value text-4xl text-center border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-colors ${
                  isBelowThreshold
                    ? "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400"
                    : isAboveThreshold
                    ? "border-forest-300 dark:border-forest-700 bg-forest-50 dark:bg-forest-900/10 text-forest-600 dark:text-forest-400"
                    : "border-border bg-background text-foreground"
                }`}
              />
              {temperature !== "" && (
                <div className="absolute right-5 top-1/2 -translate-y-1/2">
                  <span className="text-xl text-muted-foreground/50">°F</span>
                </div>
              )}
            </div>
            {isBelowThreshold && (
              <p className="text-xs text-red-500 mt-2 font-semibold flex items-center gap-1">
                <Thermometer size={11} />
                Below 131°F — does not meet EPA compliance threshold
              </p>
            )}
            {isAboveThreshold && (
              <p className="text-xs text-forest-600 dark:text-forest-400 mt-2 font-semibold flex items-center gap-1">
                <Check size={11} />
                Above 131°F — meets compliance threshold
              </p>
            )}
          </div>

          <div>
            <label className="label-caps text-muted-foreground mb-2 flex items-center gap-1.5">
              <Target size={11} />
              Probe Location
            </label>
            <div className="grid grid-cols-5 gap-2">
              {PROBE_LOCATIONS.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setProbeLocation(loc)}
                  className={`px-2 py-2.5 text-xs font-semibold rounded-xl transition-all capitalize ${
                    probeLocation === loc
                      ? "bg-forest-600 text-white shadow-sm shadow-forest-600/20"
                      : "bg-secondary text-muted-foreground hover:text-foreground border border-border hover:border-forest-200 dark:hover:border-forest-800"
                  }`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-caps text-muted-foreground mb-2 block">
              Notes (optional)
            </label>
            <textarea
              rows={2}
              placeholder="Any observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={!windrowId || !temperature || submitting || analyzingTemperature}
            className="w-full py-3.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-forest-600/20 inline-flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <UploadCloud size={16} className="animate-pulse" />
                Saving photo...
              </>
            ) : submitting ? (
              <>
                <CheckCircle2 size={16} className="animate-pulse" />
                Saving reading...
              </>
            ) : analyzingTemperature ? (
              <>
                <Sparkles size={16} className="animate-pulse" />
                Analyzing...
              </>
            ) : (
              <>
                <Check size={16} />
                Log Temperature
              </>
            )}
          </button>
          {submitError && (
            <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertTriangle size={14} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <p className="text-xs text-red-700 dark:text-red-300 font-medium">{submitError}</p>
            </div>
          )}
        </fieldset></form>
      </div>
    </div>
  );
}
